import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CommunityEvent } from "@/types/rider";
import { ShuttleStop, ShuttleTime } from "@/config/shuttleConfig";
import { API_URL } from "@/config/api";
import { getAssetPath } from "@/utils/assetUtils";
import { format, addHours } from "date-fns";

interface ShuttleItineraryProps {
  selectedEvent: CommunityEvent | null;
  eventId: string;
  pricingKey: string;
  durationHours: number;
  price: number;
  initialNotes: string;
  stops: ShuttleStop[];
  times: ShuttleTime[];
  tripDate: Date | null;
  isRoundTrip: boolean;
  handleBackNavigation: () => void;
  convertTo24Hour: (time12h: string) => string;
  timezoneOffset?: string;
}

type ShuttleStep = "location-time" | "name-phone";

export const ShuttleItinerary = ({
  selectedEvent,
  eventId,
  pricingKey,
  durationHours,
  price,
  initialNotes,
  stops,
  times,
  tripDate,
  isRoundTrip,
  handleBackNavigation,
  convertTo24Hour,
  timezoneOffset,
}: ShuttleItineraryProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [currentStep, setCurrentStep] = useState<ShuttleStep>("location-time");
  const [selectedStop, setSelectedStop] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsAgreement, setSmsAgreement] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load promo code from cookie
  React.useEffect(() => {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [cName, value] = cookie.trim().split("=");
      if (cName === "promo_code" && value) {
        setPromoCode(decodeURIComponent(value));
        break;
      }
    }
  }, []);

  const selectedStopData = stops.find((s) => s.label === selectedStop);
  const selectedTimeData = times.find((t) => t.time === selectedTime);

  const eventImage =
    selectedEvent?.image || getAssetPath("Default_Party_Bus.jpeg");

  const canContinue = selectedStop !== "" && selectedTime !== "";
  const canSubmit =
    name.trim() !== "" && phoneNumber.trim() !== "" && smsAgreement;

  const handleContinue = () => {
    if (!canContinue) return;
    setCurrentStep("name-phone");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (currentStep === "name-phone") {
      setCurrentStep("location-time");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      handleBackNavigation();
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || !selectedStopData || !selectedTimeData) return;

    setIsSubmitting(true);
    try {
      // Build naive datetime
      const dateStr = tripDate
        ? format(tripDate, "yyyy-MM-dd")
        : new Date().toISOString().split("T")[0];

      const pickupDateTime = `${dateStr}T${selectedTimeData.time}:00`;

      // Calculate dropoff as pickup + durationHours
      const [h, m] = selectedTimeData.time.split(":").map(Number);
      const pickupDate = new Date();
      pickupDate.setHours(h, m, 0);
      const dropoffDate = addHours(pickupDate, durationHours);
      const dropoffTime = format(dropoffDate, "HH:mm");
      const dropoffDateTime = `${dateStr}T${dropoffTime}:00`;

      const tripData = {
        community_event_id: eventId,
        bus_type: pricingKey,
        date: pickupDateTime,
        is_round_trip: true, // Always true for shuttles
        passenger_count: 1, // Always 1 for shuttles
        duration_hours: durationHours,
        customer_name: name,
        mobile_number: phoneNumber,
        pickup_address: selectedStopData.address,
        pickup_at: pickupDateTime,
        dropoff_at: dropoffDateTime,
        promo_code: promoCode || "",
        notes: initialNotes || `Shuttle - ${pricingKey}`,
        redirect_url: `${window.location.origin}/rider-checkout`,
      };

      console.log("Shuttle prepare request:", tripData);

      const response = await fetch(`${API_URL}trips/prepare/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tripData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Shuttle trip prepared:", result);
        navigate("/rider-trip-thank-you", {
          state: { phoneNumber, customerName: name },
        });
        if (window.innerWidth < 768) {
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to prepare trip");
      }
    } catch (error: any) {
      console.error("Shuttle prepare error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send checkout link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction > 0 ? -300 : 300, opacity: 0 }),
  };

  const direction = currentStep === "location-time" ? -1 : 1;

  return (
    <div className="min-h-screen bg-white flex flex-col font-newgrotesk">
      {/* Back Arrow */}
      <div className="px-4 py-4">
        <button onClick={handleBack} className="text-black hover:text-gray-600 transition-colors">
          <ArrowLeft className="h-6 w-6" />
        </button>
      </div>

      {/* Desktop: side-by-side | Mobile: stacked */}
      <div className="flex-1 flex flex-col md:flex-row md:max-w-5xl md:mx-auto md:w-full md:gap-8 md:px-4">
        {/* Event Image - left side on desktop only */}
        <div className="hidden md:flex md:w-1/2 md:items-start md:justify-center md:pt-8">
          <div className="rounded-2xl overflow-hidden w-full">
            <img
              src={eventImage}
              alt={selectedEvent?.name || "Event"}
              className="w-full h-[400px] object-cover rounded-2xl"
            />
          </div>
        </div>

        {/* Form content - right side on desktop */}
        <div className="flex-1 md:w-1/2">
          <AnimatePresence mode="wait" custom={direction}>
            {currentStep === "location-time" && (
              <motion.div
                key="location-time"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-1 flex flex-col p-6"
              >
                <h1 className="text-2xl font-bold text-black text-center mb-8">
                  Select A Pickup Location and Time
                </h1>

                <div className="mb-4">
                  <label className="text-sm font-medium text-black mb-2 block">
                    Pickup Location
                  </label>
                  <Select value={selectedStop} onValueChange={setSelectedStop}>
                    <SelectTrigger className="h-12 rounded-xl border-gray-300 bg-white text-black">
                      <SelectValue placeholder="Select a pickup location" />
                    </SelectTrigger>
                    <SelectContent>
                      {stops.map((stop) => (
                        <SelectItem key={stop.label} value={stop.label}>
                          {stop.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mb-6">
                  <label className="text-sm font-medium text-black mb-2 block">
                    Pickup Time
                  </label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger className="h-12 rounded-xl border-gray-300 bg-white text-black">
                      <SelectValue placeholder="Select a pickup time" />
                    </SelectTrigger>
                    <SelectContent>
                      {times.map((t) => (
                        <SelectItem key={t.time} value={t.time}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleContinue}
                  disabled={!canContinue}
                  className="w-full bg-black text-white py-4 rounded-xl text-lg font-semibold hover:bg-gray-800 disabled:opacity-50 mb-6"
                >
                  Continue
                </Button>

                {/* Event Image - mobile only */}
                <div className="rounded-2xl overflow-hidden md:hidden">
                  <img
                    src={eventImage}
                    alt={selectedEvent?.name || "Event"}
                    className="w-full h-48 object-cover rounded-2xl"
                  />
                </div>
              </motion.div>
            )}

            {currentStep === "name-phone" && (
              <motion.div
                key="name-phone"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-1 flex flex-col p-6"
              >
                <h1 className="text-2xl font-bold text-black text-center mb-8">
                  Almost Done
                </h1>

                <div className="mb-4">
                  <label className="text-sm font-medium text-black mb-2 block">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={`h-12 rounded-xl border-gray-300 ${name.trim() ? "bg-gray-100" : "bg-white"}`}
                  />
                </div>

                <div className="mb-4">
                  <label className="text-sm font-medium text-black mb-2 block">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className={`h-12 rounded-xl border-gray-300 ${phoneNumber.trim() ? "bg-gray-100" : "bg-white"}`}
                  />
                </div>

                <div className="mb-6">
                  <label className="text-sm font-medium text-black mb-2 block">
                    Referral Code
                  </label>
                  <Input
                    placeholder="Enter referral code (optional)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="h-12 rounded-xl border-gray-300 bg-white"
                  />
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                  className="w-full bg-black text-white py-4 rounded-xl text-lg font-semibold hover:bg-gray-800 disabled:opacity-50 mb-4"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    "Text Me Checkout Link"
                  )}
                </Button>

                <div className="flex items-start space-x-3 mb-4">
                  <Checkbox
                    id="shuttle-sms-agreement"
                    checked={smsAgreement}
                    onCheckedChange={(checked) => setSmsAgreement(checked === true)}
                    className="mt-1 border-gray-400 data-[state=checked]:bg-black data-[state=checked]:text-white"
                  />
                  <label
                    htmlFor="shuttle-sms-agreement"
                    className="text-xs text-gray-600 leading-relaxed cursor-pointer"
                  >
                    I agree to receive SMS messages from Disco regarding my shuttle booking. I may opt out at any time by replying STOP. Msg & data rates may apply.
                  </label>
                </div>

                <div className="text-center mb-6">
                  <a
                    href="https://www.discoveryconciergeservices.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 underline hover:text-gray-700"
                  >
                    Read Disclaimer Here
                  </a>
                </div>

                {/* Event Image - mobile only */}
                <div className="rounded-2xl overflow-hidden md:hidden">
                  <img
                    src={eventImage}
                    alt={selectedEvent?.name || "Event"}
                    className="w-full h-48 object-cover rounded-2xl"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
